import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import {
  applyAdminFilters,
  getAdminNavLinks,
  readAdminFiltersFromSearch,
  type AdminFilters,
} from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/coupons')({
  component: AdminCouponsRoute,
})

function AdminCouponsRoute() {
  const [token] = useState(getAdminToken)
  const [filters, setFilters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.adminCoupons>>['items']>([])
  const [newCode, setNewCode] = useState('')
  const [newCouponType, setNewCouponType] = useState<'promo' | 'friend_referral'>('promo')
  const [newDiscountValue, setNewDiscountValue] = useState('20')
  const [newDurationMonths, setNewDurationMonths] = useState('3')
  const [newPlan, setNewPlan] = useState<'monthly' | 'yearly' | 'all'>('monthly')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])
  const activeOnly = filters.status === 'active'

  const loadCoupons = async (adminToken: string): Promise<void> => {
    const data = await api.adminCoupons(adminToken, {
      status: activeOnly ? 'active' : 'all',
    })
    setRows(data.items)
  }

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!token) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        await loadCoupons(token)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load coupons.')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [activeOnly, token])

  const handleCreateCoupon = async (): Promise<void> => {
    if (!token) {
      return
    }

    const normalizedCode = newCode.trim().toUpperCase()
    const discountValue = Number(newDiscountValue)
    const durationMonths = Number(newDurationMonths)

    if (
      normalizedCode.length < 3 ||
      Number.isNaN(discountValue) ||
      discountValue <= 0 ||
      discountValue > 100
    ) {
      setError('Enter a valid coupon code and discount value between 1 and 100.')
      return
    }

    if (Number.isNaN(durationMonths) || durationMonths <= 0 || durationMonths > 24) {
      setError('Duration months must be between 1 and 24.')
      return
    }

    setIsLoading(true)
    setError(null)
    setNotice(null)

    try {
      await api.adminCreateCoupon(token, {
        code: normalizedCode,
        couponType: newCouponType,
        discountValue,
        durationMode: 'months',
        durationMonths,
        appliesToPlan: newPlan,
      })
      setNotice(`Coupon ${normalizedCode} created.`)
      setNewCode('')
      await loadCoupons(token)
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Failed to create coupon.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleCoupon = async (couponId: string, currentlyActive: boolean): Promise<void> => {
    if (!token) {
      return
    }

    setIsLoading(true)
    setError(null)
    setNotice(null)

    try {
      await api.adminPatchCoupon(token, couponId, {
        isActive: !currentlyActive,
      })
      setNotice(`Coupon ${currentlyActive ? 'deactivated' : 'activated'}.`)
      await loadCoupons(token)
    } catch (patchError) {
      setError(patchError instanceof Error ? patchError.message : 'Failed to update coupon.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Coupons</h1>
        <p className={styles.subtitle}>Manage promo and referral coupons used at checkout.</p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
        <div className={styles.row}>
          <label>
            Status
            <select
              className={styles.select}
              value={activeOnly ? 'active' : 'all'}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  status: event.target.value === 'active' ? 'active' : 'all',
                }))
              }
            >
              <option value="all">All</option>
              <option value="active">Active Only</option>
            </select>
          </label>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Apply Filters
          </button>
        </div>
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && (
        <section className={styles.card}>
          <h2>Create Coupon</h2>
          <div className={styles.row}>
            <input
              className={styles.input}
              type="text"
              value={newCode}
              onChange={(event) => setNewCode(event.target.value.toUpperCase())}
              placeholder="Code (e.g. FRIEND20)"
            />
            <select
              className={styles.select}
              value={newCouponType}
              onChange={(event) =>
                setNewCouponType(
                  event.target.value === 'friend_referral' ? 'friend_referral' : 'promo'
                )
              }
            >
              <option value="promo">Promo</option>
              <option value="friend_referral">Friend Referral</option>
            </select>
            <input
              className={styles.input}
              type="number"
              value={newDiscountValue}
              onChange={(event) => setNewDiscountValue(event.target.value)}
              min={1}
              max={100}
              placeholder="Discount %"
            />
            <input
              className={styles.input}
              type="number"
              value={newDurationMonths}
              onChange={(event) => setNewDurationMonths(event.target.value)}
              min={1}
              max={24}
              placeholder="Duration months"
            />
            <select
              className={styles.select}
              value={newPlan}
              onChange={(event) =>
                setNewPlan(
                  event.target.value === 'yearly' || event.target.value === 'all'
                    ? event.target.value
                    : 'monthly'
                )
              }
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="all">All</option>
            </select>
            <button
              className={styles.button}
              type="button"
              onClick={() => void handleCreateCoupon()}
              disabled={isLoading}
            >
              Create Coupon
            </button>
          </div>
          {notice && <p className={styles.notice}>{notice}</p>}
          {error && <p className={styles.error}>{error}</p>}
        </section>
      )}

      {token && !isLoading && rows.length === 0 && !error && (
        <section className={styles.card}>
          <p className={styles.emptyState}>No coupons created yet.</p>
        </section>
      )}

      {token && rows.length > 0 && (
        <section className={styles.card}>
          <h2>Coupon Catalog</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Discount</th>
                <th>Duration</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.code}</td>
                  <td>{row.couponType}</td>
                  <td>{row.discountValue}%</td>
                  <td>
                    {row.durationMode === 'months' ? `${row.durationMonths ?? 1} months` : 'once'}
                  </td>
                  <td>{row.appliesToPlan}</td>
                  <td>{row.isActive ? 'active' : 'inactive'}</td>
                  <td>
                    <button
                      className={styles.secondaryButton}
                      type="button"
                      onClick={() => void handleToggleCoupon(row.id, row.isActive)}
                      disabled={isLoading}
                    >
                      {row.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </section>
  )
}
